<script>
import moment from 'moment'
import { formatTimestamp } from '../utils/formatTimestamp'
import { api } from '../../../firebase'
import { normalizeCapitalization, underscoresToSpaces } from '../utils/normalizeEventData'
import EventsEdit from './EventsEdit'

export default {
  name: 'MobilizeV2EventDetail',
  components: {
    EventsEdit
  },
  async mounted() {
    await this.load()
  },
  data() {
    return {
      loadingState: {
        pending: false,
        done: false,
        error: null
      },
      event: null,
      eventOverrides: null,
      toggleState: {
        pending: false,
        done: false,
        error: null
      },
      rendered: '',
      showEventsEditDialog: false
    }
  },
  computed: {
    id() {
      return this.$route.params.id
    },
    start() {
      if (!this.event) {
        return undefined
      }
      const { timeslots } = this.event.data
      const firstTimeslot = timeslots && timeslots[0]
      const start = firstTimeslot && firstTimeslot.start_date
      return start && start * 1000
    },
    end() {
      if (!this.event) {
        return undefined
      }
      const { timeslots } = this.event.data
      const lastTimeslot = timeslots && timeslots[timeslots.length - 1]
      const end = lastTimeslot && lastTimeslot.end_date
      return end && end * 1000
    }
  },
  methods: {
    async load() {
      this.loadingState = {
        pending: true,
        done: false,
        error: null
      }
      try {
        const id = this.id
        const { result } = await api.events.findById({ id })
        this.event = result
        const eventOverridesResponse = await api.eventOverrides.findByEvent({ event: id }).catch(() => null)
        this.eventOverrides = eventOverridesResponse && eventOverridesResponse.result
        this.loadingState.done = true
      } catch (error) {
        this.loadingState.error = error
      } finally {
        this.loadingState.pending = false
      }
    },
    async submitEdits(overrides) {
      this.showEventsEditDialog = false
      if (!this.eventOverrides) {
        await api.eventOverrides.create({
          event: this.id,
          overrides
        })
        const { result } = await api.eventOverrides.findByEvent({ event: this.id })
        this.eventOverrides = result
      } else {
        this.eventOverrides = await api.eventOverrides.updateById({ id: this.eventOverrides.id, data: overrides })
      }
    },
    formatTimestamp,
    moment,
    normalizeCapitalization,
    underscoresToSpaces
  }
}
</script>

<template>
  <div class="mobilizev2-view detail">
    <v-row class="header-wrapper">
      <h1>Event Details</h1>
      <v-spacer />
      <v-dialog
        v-model="showEventsEditDialog"
        fullscreen
        hide-overlay
        transition="dialog-bottom-transition"
        class="filter-dialogue"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn color="primary" v-bind="attrs" v-on="on" :disabled="loadingState.pending">
            <v-icon left>edit</v-icon>
            Edit Event
          </v-btn>
        </template>
        <events-edit
          @close-dialog="showEventsEditDialog = false"
          @submit-edits="submitEdits"
          :event="event"
          :eventOverrides="eventOverrides"
        />
      </v-dialog>
    </v-row>

    <div v-if="loadingState.pending">
      <v-alert>Loading...</v-alert>
    </div>
    <div v-else-if="loadingState.error">
      <v-alert outlined color="red">
        <p>An error occurred while loading the event template.</p>
        <p>{{ loadingState.error.message }}</p>
      </v-alert>
    </div>
    <div v-else-if="loadingState.done">
      <v-simple-table>
        <template v-slot:default>
          <tbody>
            <tr>
              <td>ID</td>
              <td>{{ event.data.id }}</td>
            </tr>
            <tr>
              <td>Website</td>
              <td>
                <a :href="event.data.browser_url">
                  <td><v-icon color="#1976d1">link</v-icon></td>
                </a>
              </td>
            </tr>
            <tr>
              <td>Image</td>
              <td><img :src="event.data.featured_image_url" class="featured-image" /></td>
            </tr>
            <tr>
              <td>Title</td>
              <td>{{ event.data.title }}</td>
            </tr>
            <tr>
              <td>Description</td>
              <td>{{ event.data.description }}</td>
            </tr>
            <tr>
              <td>Summary</td>
              <td>{{ event.data.summary }}</td>
            </tr>
            <tr>
              <td>Virtual</td>
              <td>{{ event.data.is_virtual ? 'Yes' : 'No' }}</td>
            </tr>
            <tr>
              <td>Event Type</td>
              <td>{{ underscoresToSpaces(normalizeCapitalization(event.data.event_type, true)) }}</td>
            </tr>
            <tr>
              <td>City</td>
              <td>{{ event.data.location && event.data.location.locality }}</td>
            </tr>
            <tr>
              <td>State</td>
              <td>{{ event.data.location && event.data.location.region }}</td>
            </tr>
            <tr>
              <td>Map</td>
              <td><v-icon color="#1976d1">map</v-icon></td>
            </tr>
            <tr>
              <td>Start Date</td>
              <td>{{ formatTimestamp(start, 'date') }}</td>
            </tr>
            <tr>
              <td>Start Time</td>
              <td>{{ formatTimestamp(start, 'time') }}</td>
            </tr>
            <tr>
              <td>End Date</td>
              <td>{{ formatTimestamp(end, 'date') }}</td>
            </tr>
            <tr>
              <td>End Time</td>
              <td>{{ formatTimestamp(end, 'time') }}</td>
            </tr>
            <tr>
              <td>Timezone</td>
              <td>{{ event.data.timezone }}</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.featured-image {
  padding: 5px 0;
}

.header-wrapper {
  margin: 0;
}
</style>
